import { ClientMergeRequest } from "intake_shared/src/client_merge";
import { httpsCallable } from "firebase/functions";
import { fxs } from "../utils/firestore";
import {
    ClientStatus,
    MasterListStatus,
    UpdatedFrom,
} from "intake_shared/src/types";
import db from "../utils/firestore";
import { Collection } from "intake_shared/src/enum";
import {
    IArchivedClient,
    IClient,
    IMasterListSearchByOptions,
} from "intake_shared/src/types";
import { getPhoneNumberSearchVariations, getStringVariations } from "../utils/utils";
import { convertStoreDocToClient, getClientFromStoreData } from "./util";
import { Timestamp } from "firebase/firestore";
import { getFeeRevision } from "./feeRevision";

const COUNT_PER_PAGE = 100;

function mergeSourceRevisions(
    ...sourceRevisionMaps: Array<Record<string, string> | undefined>
): Record<string, string> {
    return sourceRevisionMaps.reduce<Record<string, string>>((merged, revisions) => {
        Object.entries(revisions ?? {}).forEach(([entityId, revision]) => {
            if (!merged[entityId] || revision > merged[entityId]) {
                merged[entityId] = revision;
            }
        });
        return merged;
    }, {});
}

function getFeeFields(client: Partial<IClient>): Partial<IClient> {
    const feeFields: Partial<IClient> = {};
    if (client[ClientStatus.FEE_SETTING] !== undefined) {
        feeFields[ClientStatus.FEE_SETTING] = client[ClientStatus.FEE_SETTING];
    }
    if (client.clinician_fees !== undefined) {
        feeFields.clinician_fees = client.clinician_fees;
    }
    if (client.fees_update_details !== undefined) {
        feeFields.fees_update_details = client.fees_update_details;
    }
    if (client.fees_updated_by !== undefined) {
        feeFields.fees_updated_by = client.fees_updated_by;
    }
    return feeFields;
}

export async function searchMasterList(
    status?: MasterListStatus,
    searchOptions?: IMasterListSearchByOptions,
    last_doc?: any
): Promise<(IClient | IArchivedClient)[]> {
    const {
        email,
        firstname,
        lastname,
        phoneno,
        date_of_birth,
        tagKeys = [],
    } = searchOptions || {};
    const hasFieldSearch = Boolean(
        email || firstname || lastname || phoneno || date_of_birth
    );
    const isValidSearch = Boolean(status || hasFieldSearch);
    if (!isValidSearch) {
        return [];
    }

    let results: (IClient | IArchivedClient)[] = [];

    async function searchCollection(
        collectionName: string,
        queryStatus: MasterListStatus
    ) {
        let dbQuery: any = db.collection(collectionName);
        if (tagKeys.length)
            dbQuery = dbQuery.where(
                "active_tag_keys",
                "array-contains-any",
                tagKeys
            );

        if (!hasFieldSearch) {
            dbQuery = dbQuery.orderBy("date_updated", "desc");
        }

        if (email) {
            dbQuery = dbQuery.where(
                "email",
                "in",
                getStringVariations(email.trim())
            );
        } else if (firstname) {
            dbQuery = dbQuery.where(
                "first_name",
                "in",
                getStringVariations(firstname)
            );
        } else if (lastname) {
            dbQuery = dbQuery.where(
                "last_name",
                "in",
                getStringVariations(lastname)
            );
        } else if (phoneno) {
            dbQuery = dbQuery.where(
                "phone_no",
                "in",
                getPhoneNumberSearchVariations(phoneno)
            );
        } else if (date_of_birth) {
            // This query is to include dates greater than or equal to the user-provided date of birth and
            // less than the day after. So, it effectively covers the entire day of the user's date of birth, this is because
            // the time in the firestore time stamp and the time in the provided date will be different
            // and we really just want to compare the date.
            dbQuery = dbQuery
                .where("date_of_birth", ">=", date_of_birth)
                .where(
                    "date_of_birth",
                    "<",
                    new Date(date_of_birth.getTime() + 24 * 60 * 60 * 1000)
                );
        } else {
            if (queryStatus === "active") {
                dbQuery = dbQuery.where("status", "==", "Appointment Confirmed");
            }
            dbQuery = dbQuery.limit(COUNT_PER_PAGE);
        }
        const querySnapshot = await dbQuery.get();
        return querySnapshot.docs.map((d: any) => d.exists && d.data());
    }

    if (status === "ALL") {
        const currentClients = await searchCollection(Collection.CLIENTS, "active");
        results.push(...currentClients);

        const historyClients = await searchCollection(
            Collection.HISTORY,
            "inactive"
        );
        results.push(...historyClients);
    } else {
        let collectionName: string = "";

        if (status === "active") {
            collectionName = Collection.CLIENTS;
        } else if (status === "inactive") {
            collectionName = Collection.HISTORY;
        }
        if (collectionName) {
            results.push(...(await searchCollection(collectionName, status)));
        }
    }
    return results.map(getClientFromStoreData);
}

export async function deleteHistory(clientId: string) {
    await db.collection(Collection.HISTORY).doc(clientId).delete();
}

export async function fetchArchivedClientsByEmails(
    emails: string[]
): Promise<IArchivedClient[]> {
    const raw_data = (
        await (
            await db.collection(Collection.HISTORY).where("email", "in", emails)
        ).get()
    ).docs;
    return raw_data.map(convertStoreDocToClient) as IArchivedClient[];
}

export async function getClientHistoryById(
    id: string
): Promise<IArchivedClient | undefined> {
    const client_doc = await db.collection(Collection.HISTORY).doc(id).get();
    if (client_doc.exists) {
        return convertStoreDocToClient(client_doc) as IArchivedClient;
    }
    return;
}

export async function updateArchivedClientCompletionReason(
    clientId: string,
    reason: string,
    lastUpdatedBy: string
): Promise<boolean> {
    if (!reason.trim() || !lastUpdatedBy) {
        throw new Error("A completion reason and user are required");
    }

    return db.runTransaction(async (transaction) => {
        const clientRef = db.collection(Collection.HISTORY).doc(clientId);
        const snapshot = await transaction.get(clientRef);
        if (!snapshot.exists) {
            throw new Error("Completed client not found");
        }

        const client = snapshot.data() as IArchivedClient;
        if (client.status !== ClientStatus.COMPLETED) {
            throw new Error("Client is no longer completed");
        }
        if (client.complete_notes?.reason === reason) {
            return false;
        }

        transaction.update(clientRef, {
            "complete_notes.reason": reason,
            last_updated_by: lastUpdatedBy,
            date_updated: Timestamp.fromDate(new Date()),
            updated_from: UpdatedFrom.INTAKE,
        });
        return true;
    });
}

export async function updateArchivedClient(
    id: string,
    client: IArchivedClient,
    saveFeeChanges = false
) {
    const now = new Date();
    const client_doc = {
        ...client,
        ...(client?.email && { email: client.email.toLowerCase().trim() }),
        date_of_birth: client?.date_of_birth
            ? Timestamp.fromDate(client.date_of_birth as Date)
            : null,
        date_updated: Timestamp.fromDate(now),
        updated_from: UpdatedFrom.INTAKE,
    };
    delete client_doc.tags;
    delete client_doc.active_tag_keys;
    delete client_doc.old_ids;
    delete client_doc.mantle_session_source_revisions;
    delete client_doc.mantle_supervisor_intake_source_revisions;
    delete client_doc.mantle_fee_source_revision;
    delete client_doc.loaded_fee_revision;
    if (!saveFeeChanges) {
        delete client_doc[ClientStatus.FEE_SETTING];
        delete client_doc.clinician_fees;
        delete client_doc.fees_update_details;
        delete client_doc.fees_updated_by;
    }

    if (client.status === ClientStatus.COMPLETED) {
        const historyRef = db.collection(Collection.HISTORY).doc(id);
        await db.runTransaction(async (transaction) => {
            if (saveFeeChanges) {
                const historySnapshot = await transaction.get(historyRef);
                if (!historySnapshot.exists) {
                    throw new Error("Completed client not found");
                }
                const persistedClient = historySnapshot.data() as IArchivedClient;
                if (
                    getFeeRevision(
                        persistedClient.fees_update_details?.updated_at
                    ) !== getFeeRevision(client.loaded_fee_revision)
                ) {
                    throw new Error(
                        "Client fees changed after this chart was loaded. " +
                            "Refresh and try again."
                    );
                }
                client_doc.fees_update_details = {
                    ...(client.fees_update_details ?? {}),
                    updated_by:
                        client.fees_update_details?.updated_by ??
                        client.last_updated_by ??
                        "",
                    updated_at: now,
                };
            }
            transaction.set(historyRef, client_doc, { merge: true });
        });
        if (saveFeeChanges) {
            client.loaded_fee_revision = now.toISOString();
            client.fees_update_details = client_doc.fees_update_details;
        }
        return { reactivated: false };
    }

    const historyRef = db.collection(Collection.HISTORY).doc(id);
    const clientRef = db.collection(Collection.CLIENTS).doc(id);

    await db.runTransaction(async (transaction) => {
        const activeClientDoc = await transaction.get(clientRef);
        if (
            activeClientDoc.exists &&
            activeClientDoc.data()?.status !== ClientStatus.COMPLETED
        ) {
            throw new Error(
                "Cannot reactivate archived client: active client already exists"
            );
        }

        const historyClientDoc = await transaction.get(historyRef);
        if (!historyClientDoc.exists) {
            throw new Error(
                "Cannot reactivate archived client: archived client does not exist"
            );
        }

        const historyClient = historyClientDoc.data() as IArchivedClient;
        const existingCompletedData = activeClientDoc.exists
            ? (activeClientDoc.data() as IClient)
            : null;
        const persistedFeeClient =
            existingCompletedData &&
            (getFeeRevision(existingCompletedData.fees_update_details?.updated_at) ??
                "") >
                (getFeeRevision(historyClient.fees_update_details?.updated_at) ?? "")
                ? existingCompletedData
                : historyClient;

        if (
            saveFeeChanges &&
            getFeeRevision(persistedFeeClient.fees_update_details?.updated_at) !==
                getFeeRevision(client.loaded_fee_revision)
        ) {
            throw new Error(
                "Client fees changed after this chart was loaded. Refresh and try again."
            );
        }
        if (saveFeeChanges) {
            client_doc.fees_update_details = {
                ...(client.fees_update_details ?? {}),
                updated_by:
                    client.fees_update_details?.updated_by ??
                    client.last_updated_by ??
                    "",
                updated_at: now,
            };
        }

        const statusHistory = {
            ...(existingCompletedData?.status_history || {}),
            ...(historyClient.status_history || {}),
            ...(client.status_history || {}),
            [client.status]: Timestamp.fromDate(now),
        };
        const mantleSessionSourceRevisions = mergeSourceRevisions(
            existingCompletedData?.mantle_session_source_revisions,
            historyClient.mantle_session_source_revisions
        );
        const mantleSupervisorIntakeSourceRevisions = mergeSourceRevisions(
            existingCompletedData?.mantle_supervisor_intake_source_revisions,
            historyClient.mantle_supervisor_intake_source_revisions
        );
        const mantleFeeSourceRevision = [
            existingCompletedData?.mantle_fee_source_revision,
            historyClient.mantle_fee_source_revision,
        ]
            .filter((revision): revision is string => Boolean(revision))
            .sort()
            .pop();

        const { archived_on: _archived_on, ...reactivatedClientDoc } = client_doc;
        const {
            archived_on: _existing_archived_on,
            ...existingCompletedDataWithoutArchival
        } = (existingCompletedData || {}) as IArchivedClient;
        transaction.set(clientRef, {
            ...existingCompletedDataWithoutArchival,
            ...reactivatedClientDoc,
            tags: historyClient.tags ?? {},
            active_tag_keys: historyClient.active_tag_keys ?? [],
            old_ids: historyClient.old_ids ?? [],
            ...(!saveFeeChanges ? getFeeFields(persistedFeeClient) : {}),
            status_history: statusHistory,
            mantle_session_source_revisions: mantleSessionSourceRevisions,
            mantle_supervisor_intake_source_revisions:
                mantleSupervisorIntakeSourceRevisions,
            ...(mantleFeeSourceRevision
                ? { mantle_fee_source_revision: mantleFeeSourceRevision }
                : {}),
        });
        transaction.delete(historyRef);
    });

    if (saveFeeChanges) {
        client.loaded_fee_revision = now.toISOString();
        client.fees_update_details = client_doc.fees_update_details;
    }

    return { reactivated: true };
}

export async function mergeClientFromArchive(data: ClientMergeRequest) {
    await httpsCallable<ClientMergeRequest, void>(
        fxs,
        "httpFunctionMergeClientTags"
    )(data);
    return true;
}
